import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { callEdgeFunction, supabase } from './supabaseClient';
import {
  fetchSimpraCheck,
  buildDiscountGroupLookupIndex,
  resolveEffectiveAppliedDiscountId,
} from './simpraCheck';
import { SimpraCheckDetailPanel } from './SimpraCheckDetailPanel';
import './OrdersPage.css';

const PAGE_SIZE = 20;

const RANGE_OPTS = [
  { id: 'all', label: 'Tümü', days: null },
  { id: '7d', label: '7 gün', days: 7 },
  { id: '30d', label: '30 gün', days: 30 },
  { id: '90d', label: '90 gün', days: 90 },
];

const fmtMoney = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));

const fmtDate = (val) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleString('tr-TR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const fmtDay = (val) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return '—';
  }
};

const fmtTime = (val) => {
  if (!val) return '';
  try {
    return new Date(val).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const shortId = (id) => {
  if (!id) return '—';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
};

const isUuid = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(v || '').trim(),
  );

const shopShort = (name) =>
  String(name || '')
    .replace(/^MAKE\s*ME\s*JOI\s*/i, '')
    .trim() || name;

function rangeStartIso(days) {
  if (!days) return null;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (days - 1));
  return d.toISOString();
}

function CustomerCell({ creatorId, info, compact = false }) {
  if (!creatorId) return <span className="orders-muted">—</span>;
  const name = info?.full_name || info?.email?.split('@')[0] || shortId(creatorId);
  const initial = String(name).charAt(0).toUpperCase();
  return (
    <div className={`orders-customer${compact ? ' orders-customer--compact' : ''}`} title={creatorId}>
      <span className="orders-avatar" aria-hidden>{initial}</span>
      <span className="orders-customer-text">
        <span className="orders-customer-name">{name}</span>
        {!compact && info?.email ? (
          <span className="orders-customer-meta">{info.email}</span>
        ) : null}
        {!compact && !info ? (
          <span className="orders-customer-meta orders-mono">{shortId(creatorId)}</span>
        ) : null}
      </span>
    </div>
  );
}

function Stars({ rating }) {
  if (rating == null) return <span className="orders-muted">—</span>;
  const n = Number(rating);
  if (!Number.isFinite(n)) return <span className="orders-muted">—</span>;
  const filled = Math.max(0, Math.min(5, Math.round(n)));
  return (
    <span className="orders-stars" title={`${n}/5`} aria-label={`${n} yıldız`}>
      <span className="orders-stars-filled">{'★'.repeat(filled)}</span>
      <span className="orders-stars-empty">{'★'.repeat(5 - filled)}</span>
      <span className="orders-stars-num">{n}</span>
    </span>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="orders-detail-row">
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}

export default function OrdersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState('all');
  const [shopFilter, setShopFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userById, setUserById] = useState(new Map());
  const [ratingByQr, setRatingByQr] = useState(new Map());
  const [shops, setShops] = useState([]);
  const [stats, setStats] = useState({ count: 0, paid: 0, discount: 0, original: 0 });
  const [userFilter, setUserFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [copied, setCopied] = useState('');
  const [checkCache, setCheckCache] = useState(() => new Map());
  const [discountGroupsList, setDiscountGroupsList] = useState([]);

  const groupIndex = useMemo(
    () => buildDiscountGroupLookupIndex(discountGroupsList),
    [discountGroupsList],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: gErr } = await supabase
          .from('simpra_discount_groups')
          .select(`
            global_discount_id,
            simpra_discount_group_members (
              property_id,
              property_discount_id,
              shop_label
            )
          `);
        if (!cancelled && !gErr) setDiscountGroupsList(data || []);
      } catch {
        /* optional */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadSimpraCheck = useCallback(async (checkId, propertyId) => {
    setCheckCache((prev) => {
      const existing = prev.get(checkId);
      if (existing?.loading || existing?.data) return prev;
      const next = new Map(prev);
      next.set(checkId, { loading: true, error: null, data: null });
      return next;
    });
    try {
      const data = await fetchSimpraCheck(checkId, propertyId);
      setCheckCache((prev) => {
        const next = new Map(prev);
        next.set(checkId, { loading: false, error: null, data });
        return next;
      });
    } catch (e) {
      setCheckCache((prev) => {
        const next = new Map(prev);
        next.set(checkId, {
          loading: false,
          error: e?.message || 'Check yüklenemedi',
          data: null,
        });
        return next;
      });
    }
  }, []);

  const handleAppliedSaved = useCallback((qrRowId, propertyDiscountId) => {
    if (!qrRowId || !propertyDiscountId) return;
    setRows((prev) => prev.map((row) => (
      row.id === qrRowId
        ? { ...row, applied_discount_id: propertyDiscountId }
        : row
    )));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get('user');
    const next = uid && isUuid(uid) ? uid : null;
    setUserFilter((prev) => {
      if (prev === next) return prev;
      setPage(0);
      return next;
    });
  }, [location.search]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 280);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('shops')
        .select('id, shop, simpra_property_id')
        .order('id', { ascending: true });
      if (!active) return;
      setShops(data || []);
    })();
    return () => { active = false; };
  }, []);

  const shopByProperty = useMemo(() => {
    const map = new Map();
    shops.forEach((s) => {
      const pid = String(s.simpra_property_id || '').toLowerCase();
      if (pid) map.set(pid, s);
    });
    return map;
  }, [shops]);

  const applyCommonFilters = useCallback((query) => {
    let q = query.eq('status', 'used');
    if (userFilter) q = q.eq('creator_id', userFilter);
    const since = rangeStartIso(RANGE_OPTS.find((r) => r.id === range)?.days);
    if (since) q = q.gte('used_at', since);
    if (shopFilter !== 'all') q = q.eq('property_id', shopFilter);
    const term = search.trim();
    if (term) {
      if (isUuid(term)) {
        q = q.or(`id.eq.${term},creator_id.eq.${term},check_id.eq.${term}`);
      } else {
        q = q.ilike('option', `%${term}%`);
      }
    }
    return q;
  }, [userFilter, range, shopFilter, search]);

  const fetchStats = useCallback(async () => {
    const pageSize = 1000;
    let from = 0;
    let count = 0;
    let paid = 0;
    let discount = 0;
    let original = 0;
    for (;;) {
      let q = supabase
        .from('qr_codes')
        .select('total_amount, discount_amount, original_amount')
        .range(from, from + pageSize - 1);
      q = applyCommonFilters(q);
      const { data, error: err } = await q;
      if (err) throw err;
      const batch = data || [];
      for (const row of batch) {
        count += 1;
        const p = Number(row.total_amount);
        const d = Number(row.discount_amount);
        const o = Number(row.original_amount);
        if (Number.isFinite(p)) paid += p;
        if (Number.isFinite(d)) discount += d;
        if (Number.isFinite(o)) original += o;
      }
      if (batch.length < pageSize) break;
      from += pageSize;
    }
    return { count, paid, discount, original };
  }, [applyCommonFilters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('qr_codes')
        .select(
          'id, creator_id, status, option, quantity, total_amount, discount_amount, original_amount, property_id, check_id, used_at, created_at, applied_discount_id',
          { count: 'exact' },
        )
        .order('used_at', { ascending: false, nullsFirst: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      query = applyCommonFilters(query);

      const [listResult, aggregates] = await Promise.all([
        query,
        fetchStats().catch(() => null),
      ]);

      const { data, error: qErr, count } = listResult;
      if (qErr) throw qErr;
      const list = data || [];
      setRows(list);
      setTotal(count);
      if (aggregates) setStats(aggregates);

      const creatorIds = [...new Set(list.map((r) => r.creator_id).filter(Boolean))];
      if (userFilter && !creatorIds.includes(userFilter)) creatorIds.push(userFilter);

      const map = new Map();
      if (creatorIds.length) {
        try {
          const users = await callEdgeFunction('admin-get-users-public-info', {
            method: 'POST',
            body: { p_ids: creatorIds },
          });
          (users || []).forEach((u) => map.set(u.user_id, u));
        } catch {
          /* ignore */
        }
      }
      setUserById(map);

      const qrIds = list.map((r) => r.id).filter(Boolean);
      const ratings = new Map();
      if (qrIds.length) {
        try {
          const { data: ratingRows, error: rErr } = await supabase
            .from('order_ratings')
            .select('qr_code_id, rating, comment')
            .in('qr_code_id', qrIds);
          if (!rErr) {
            (ratingRows || []).forEach((r) => ratings.set(r.qr_code_id, r));
          }
        } catch {
          /* optional table */
        }
      }
      setRatingByQr(ratings);
      setExpandedId((prev) => (prev && list.some((r) => r.id === prev) ? prev : null));
    } catch (e) {
      setError(e?.message || 'Siparişler yüklenemedi.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, applyCommonFilters, fetchStats, userFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredUserLabel = useMemo(() => {
    if (!userFilter) return null;
    const info = userById.get(userFilter);
    if (!info) return shortId(userFilter);
    return info.full_name || info.email || shortId(userFilter);
  }, [userFilter, userById]);

  const pageCount = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;
  const saveRate = stats.original > 0
    ? Math.round((stats.discount / stats.original) * 100)
    : null;

  const shopFilterOptions = useMemo(() => {
    const opts = [{ id: 'all', label: 'Tüm şubeler' }];
    shops.forEach((s) => {
      if (!s.simpra_property_id) return;
      opts.push({
        id: String(s.simpra_property_id),
        label: shopShort(s.shop) || `Mağaza #${s.id}`,
      });
    });
    return opts;
  }, [shops]);

  const clearUserFilter = () => {
    navigate({ pathname: '/dashboard', search: '?section=orders' }, { replace: true });
  };

  const copyText = async (value, key) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(key);
      window.setTimeout(() => setCopied(''), 1400);
    } catch {
      /* ignore */
    }
  };

  const resolveShop = (propertyId) => {
    if (!propertyId) return null;
    return shopByProperty.get(String(propertyId).toLowerCase()) || null;
  };

  return (
    <div className="admin-page-content orders-page">
      <header className="orders-hero">
        <div className="orders-hero-copy">
          <p className="orders-kicker">Uygulama yönetimi</p>
          <h1 className="orders-title">Siparişler</h1>
          <p className="orders-sub">
            Tamamlanmış QR siparişleri — müşteri, şube, ödeme ve Simpra check detayı.
          </p>
        </div>
        <button
          type="button"
          className="orders-refresh"
          onClick={fetchData}
          disabled={loading}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0114.13-3.36L23 10M1 14l5.36 4.36A9 9 0 0020.49 15" />
          </svg>
          {loading ? '…' : 'Yenile'}
        </button>
      </header>

      <div className="orders-spotlight" aria-label="Özet">
        <article className="orders-spot">
          <p className="orders-spot-label">Sipariş</p>
          <p className="orders-spot-value">{loading ? '…' : fmtNum(stats.count)}</p>
          <p className="orders-spot-meta">
            {range === 'all' ? 'Tüm zamanlar' : RANGE_OPTS.find((r) => r.id === range)?.label}
            {userFilter ? ' · filtrelenmiş kullanıcı' : ''}
          </p>
        </article>
        <article className="orders-spot orders-spot--money">
          <p className="orders-spot-label">Ödenen</p>
          <p className="orders-spot-value">{loading ? '…' : fmtMoney(stats.paid)}</p>
          <p className="orders-spot-meta">Orijinal {fmtMoney(stats.original)}</p>
        </article>
        <article className="orders-spot orders-spot--accent">
          <p className="orders-spot-label">Verilen indirim</p>
          <p className="orders-spot-value">{loading ? '…' : fmtMoney(stats.discount)}</p>
          <p className="orders-spot-meta">
            {saveRate != null ? `%${saveRate} oran` : 'İndirim toplamı'}
          </p>
        </article>
      </div>

      <div className="orders-controls">
        <div className="orders-search-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input
            type="search"
            className="orders-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Seçenek, sipariş veya kullanıcı ID…"
            aria-label="Sipariş ara"
          />
        </div>

        <div className="orders-seg" role="tablist" aria-label="Dönem">
          {RANGE_OPTS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={range === opt.id}
              className={`orders-seg-btn${range === opt.id ? ' is-active' : ''}`}
              onClick={() => { setRange(opt.id); setPage(0); }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {shopFilterOptions.length > 1 ? (
          <div className="orders-shop-filters" role="tablist" aria-label="Şube">
            {shopFilterOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={shopFilter === opt.id}
                className={`orders-chip${shopFilter === opt.id ? ' is-active' : ''}`}
                onClick={() => { setShopFilter(opt.id); setPage(0); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        ) : null}

        {userFilter ? (
          <div className="orders-user-chip">
            <span>Kullanıcı: <strong>{filteredUserLabel}</strong></span>
            <button type="button" onClick={clearUserFilter}>Temizle</button>
          </div>
        ) : null}

        <p className="orders-controls-meta">
          {loading ? 'Yükleniyor…' : `${fmtNum(total ?? 0)} kayıt`}
        </p>
      </div>

      {error ? <p className="orders-alert" role="alert">{error}</p> : null}

      <div className="orders-list" role="list">
        {loading ? (
          <div className="orders-skel" aria-busy="true">
            <div /><div /><div /><div />
          </div>
        ) : rows.length === 0 ? (
          <div className="orders-empty-card">
            <p className="orders-empty-title">Sipariş bulunamadı</p>
            <p className="orders-empty-sub">
              {userFilter || search || range !== 'all' || shopFilter !== 'all'
                ? 'Filtreleri gevşetin veya başka bir dönem seçin.'
                : 'Kullanılmış QR siparişleri burada listelenir.'}
            </p>
          </div>
        ) : (
          rows.map((row) => {
            const shop = resolveShop(row.property_id);
            const shopName = shop ? shopShort(shop.shop) : null;
            const rating = ratingByQr.get(row.id);
            const info = userById.get(row.creator_id);
            const expanded = expandedId === row.id;
            const discount = Number(row.discount_amount);
            const original = Number(row.original_amount);
            const rowSave = Number.isFinite(discount) && Number.isFinite(original) && original > 0
              ? Math.round((discount / original) * 100)
              : null;
            const when = row.used_at || row.created_at;

            return (
              <article
                key={row.id}
                className={`orders-card${expanded ? ' is-open' : ''}`}
                role="listitem"
              >
                <button
                  type="button"
                  className="orders-card-main"
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                  aria-expanded={expanded}
                >
                  <div className="orders-card-when">
                    <strong>{fmtDay(when)}</strong>
                    <span>{fmtTime(when)}</span>
                  </div>

                  <div className="orders-card-who">
                    <CustomerCell creatorId={row.creator_id} info={info} compact />
                  </div>

                  <div className="orders-card-shop">
                    {shopName ? (
                      <span className="orders-shop-pill">{shopName}</span>
                    ) : (
                      <span className="orders-muted">Şube yok</span>
                    )}
                    <span className="orders-option-line">
                      {row.option || 'Sipariş'}
                      {row.quantity > 1 ? ` · ×${row.quantity}` : ''}
                    </span>
                  </div>

                  <div className="orders-card-money">
                    <strong>{fmtMoney(row.total_amount)}</strong>
                    <span>
                      {Number.isFinite(discount) && discount > 0
                        ? `${fmtMoney(discount)} TL indirim${rowSave != null ? ` · %${rowSave}` : ''}`
                        : 'İndirim yok'}
                    </span>
                  </div>

                  <div className="orders-card-rating">
                    <Stars rating={rating?.rating} />
                  </div>

                  <span className={`orders-chevron${expanded ? ' is-open' : ''}`} aria-hidden>
                    ▾
                  </span>
                </button>

                {expanded ? (
                  <div className="orders-card-detail">
                    <div className="orders-detail-grid">
                      <DetailRow label="Sipariş ID">
                        <code className="orders-mono">{row.id}</code>
                        <button type="button" className="orders-copy" onClick={() => copyText(row.id, row.id)}>
                          {copied === row.id ? 'Kopyalandı' : 'Kopyala'}
                        </button>
                      </DetailRow>
                      <DetailRow label="Tarih">
                        <span>{fmtDate(when)}</span>
                      </DetailRow>
                      <DetailRow label="Müşteri">
                        <CustomerCell creatorId={row.creator_id} info={info} />
                        {row.creator_id ? (
                          <button
                            type="button"
                            className="orders-copy"
                            onClick={() => {
                              navigate(`/dashboard?section=orders&user=${encodeURIComponent(row.creator_id)}`);
                              setPage(0);
                            }}
                          >
                            Bu kullanıcının siparişleri
                          </button>
                        ) : null}
                      </DetailRow>
                      <DetailRow label="Şube">
                        <span>{shop?.shop || (row.property_id ? shortId(row.property_id) : '—')}</span>
                      </DetailRow>
                      <DetailRow label="Ödenen / orijinal">
                        <span>
                          {fmtMoney(row.total_amount)}
                          {' / '}
                          {fmtMoney(row.original_amount)}
                        </span>
                      </DetailRow>
                      <DetailRow label="İndirim">
                        <span>
                          {fmtMoney(row.discount_amount)}
                          {rowSave != null ? ` (%${rowSave})` : ''}
                        </span>
                      </DetailRow>
                      <DetailRow label="Check ID">
                        {row.check_id ? (
                          <>
                            <code className="orders-mono">{row.check_id}</code>
                            <button
                              type="button"
                              className="orders-copy"
                              onClick={() => copyText(row.check_id, `c-${row.id}`)}
                            >
                              {copied === `c-${row.id}` ? 'Kopyalandı' : 'Kopyala'}
                            </button>
                          </>
                        ) : (
                          <span className="orders-muted">—</span>
                        )}
                      </DetailRow>
                      <DetailRow label="Uygulanan indirim">
                        {row.applied_discount_id ? (
                          <code className="orders-mono">{shortId(row.applied_discount_id)}</code>
                        ) : (
                          <span className="orders-muted">—</span>
                        )}
                      </DetailRow>
                      <DetailRow label="Puan">
                        <Stars rating={rating?.rating} />
                        {rating?.comment ? (
                          <span className="orders-rating-comment">{rating.comment}</span>
                        ) : null}
                      </DetailRow>
                    </div>

                    <div className="orders-check-panel">
                      <SimpraCheckDetailPanel
                        checkId={row.check_id}
                        cacheEntry={row.check_id ? checkCache.get(row.check_id) : null}
                        onLoad={loadSimpraCheck}
                        assignedDiscountId={info?.discount_id}
                        appliedDiscountId={resolveEffectiveAppliedDiscountId({
                          storedAppliedId: row.applied_discount_id,
                          assignedGlobalId: info?.discount_id,
                          propertyId: row.property_id,
                          groupIndex,
                        })}
                        storedAppliedId={row.applied_discount_id}
                        groupIndex={groupIndex}
                        propertyId={row.property_id}
                        qrRowId={row.id}
                        onAppliedSaved={handleAppliedSaved}
                      />
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </div>

      {total != null && total > PAGE_SIZE ? (
        <div className="orders-pager">
          <button
            type="button"
            className="orders-btn"
            disabled={loading || page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Önceki
          </button>
          <span>
            {page + 1} / {pageCount}
          </span>
          <button
            type="button"
            className="orders-btn"
            disabled={loading || page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
          >
            Sonraki
          </button>
        </div>
      ) : null}
    </div>
  );
}
